import * as React from 'react';

import Menu from '@mui/material/Menu';
import { alpha, styled } from '@mui/material/styles';

import { IconButton, Tooltip, Checkbox } from "@mui/material";
import { ReactComponent as MoreOptionsIcon } from './icons/more_3_dots.svg';
import { ReactComponent as MarkUnreadDisabled } from './icons/mark_unread_disabled.svg';
import { ReactComponent as MarkUnread } from './icons/mark_unread.svg';
import { ReactComponent as NotificationBellOff } from './icons/notification_bell_off_new.svg';
import { ReactComponent as NotificationBellOffGray } from './icons/notification_bell_off_gray_new.svg';
import { ReactComponent as NotificationBellOn } from './icons/notification_bell_on_new.svg';
import { ReactComponent as NewNote } from './icons/new_note.svg';
import { ReactComponent as NewNoteDisabled } from './icons/new_note_disabled.svg';
import InternalTagsMenu from './InternalTagsMenu'
import { ReactComponent as ViewParticipants } from './icons/view_all_participants.svg';
import { ReactComponent as ViewParticipantsDisabled } from './icons/view_all_participants_disabled.svg';
import LinkIcon from '@mui/icons-material/Link';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import ForwardConversationDialog from './ForwardConversationDialog';
import { ReactComponent as LinkOutIcon } from './icons/link_out.svg';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import { ReactComponent as PauseAutoCommunications } from './icons/pause_notifications.svg';
import { ReactComponent as UnpauseAutoCommunications } from './icons/unpause_notifications.svg';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import useCloseOnPanelChange from './utils/useCloseOnPanelChange';
import { cv } from '../themeVars';
import { aiAutoRepliesOn, andAiAutoReplies } from './notificationPauseLabels';

const StyledMenu = styled((props) => (
    <Menu
        elevation={0}
        // CP-6445: MUI locks body scroll while open; that lock can outlive the menu
        // when a route change switches panels without a dismissing click.
        disableScrollLock
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        maxWidth: 330,
        backgroundColor: cv('surface', "#FCFCFE"),
        color:
            theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            paddingTop: "2px",
            paddingBottom: "1px",
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                //color: theme.palette.text.secondary,
                //marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity,
                ),
            },
        },
        '& .MuiFormControlLabel-root': {
            width: "120%",
            marginRight: 0,
            paddingRight: 16,
            '& .MuiTypography-root': {
                fontSize: ".9em",
                fontWeight: 500,
            },

        },
    },
    '& .MuiInputBase-root': {
        '& .MuiAutocomplete-input': {
            width: "fit-content !important"
        },
    },

}));

export default function MoreOptionsMenu(props) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [, setInternalTags] = React.useState(props.internalTags || [])
    const [forceInternalTagsClick, setForceInternalTagsClick] = React.useState(null)
    const [forwardOpen, setForwardOpen] = React.useState(false)
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    useCloseOnPanelChange(props.selectedScreen, handleClose);

    React.useEffect(() => {
        setInternalTags(props.internalTags)
    }, [props.internalTags]);

    // A different chat: the forward dialog belonged to the previous one.
    React.useEffect(() => {
        setForwardOpen(false)
    }, [props.channelUniqueName]);

    const canForward = Boolean(props.channelSubjectID && props.channelUniqueName && props.userIsGuest === false);

    return (
        <span>
            <Tooltip title={'Filter/Hide More Options'} arrow placement="top"><IconButton id="demo-customized-button"
                aria-controls={open ? 'demo-customized-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                variant="contained"
                style={{ ...props.style, cursor: props.channelSubjectID ? 'pointer' : "default", marginLeft: "-.45rem", width: "2.25rem", height: "2.25rem" }}
            >
                {<MoreOptionsIcon />}
            </IconButton></Tooltip>

            <StyledMenu
                id="demo-customized-menu"
                MenuListProps={{
                    'aria-labelledby': 'demo-customized-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <MenuList style={{minWidth: "210px"}}>
                    {/* add a junk tag since this key press causes outline bug in mui*/}
                    {props.messages && props.messages.length > 0 && !(props.channel?.lastReadMessageIndex === null || props.channel?.lastReadMessageIndex < props.channel?.lastMessage?.index) ?
                        <MenuItem onClick={e => {props.markConversationUnread(e); handleClose()}}>
                            <span>
                                <IconButton style={{ width: "2.5rem", height: "2.5rem", cursor: 'pointer' }}>
                                    <MarkUnread style={{ fontSize: "2.5em" }} />
                                </IconButton>
                                <span style={{ fontWeight: 400, fontSize: ".9rem" }}>Mark as unread</span>
                            </span>
                        </MenuItem>
                        :
                        <MenuItem>
                            <span>
                                <IconButton style={{ width: "2.5rem", height: "2.5rem", cursor: 'default' }}>
                                    <MarkUnreadDisabled style={{ fontSize: "2.5em" }} />
                                </IconButton>
                                <span style={{ fontWeight: 400, fontSize: ".9rem" }}>Mark as unread</span>
                            </span>
                        </MenuItem>

                    }
                    <MenuItem onClick={e => props.handleToggleNotificationLevel(e)}>
                        <IconButton style={{ width: "2.5rem", height: "2.5rem" }}>
                            {props.notificationLevel == null ?
                                <NotificationBellOffGray style={{ fontSize: '2.5em' }} />
                                :
                                props.notificationLevel === 'default' ?
                                    <NotificationBellOff style={{ fontSize: "2.5em" }} />
                                    :
                                    <NotificationBellOn style={{ fontSize: "2.5em" }} />
                            }
                        </IconButton>
                        <span style={{ fontWeight: 400, fontSize: ".9rem" }}>{(props.notificationLevel === 'default' ? 'Mute' : 'Unmute') + " notifications"}</span>
                    </MenuItem>
                    {props.gtuNotificationsPaused != null &&
                        <MenuItem onClick={e => props.handleToggleGtuNotifications(e)} title={props.gtuNotificationsPaused === false ? `Pause all notifications${andAiAutoReplies()} for this guest on this tour${aiAutoRepliesOn() ? ' (the AI still suggests replies)' : ''}` : `Unpause all notifications${andAiAutoReplies()} for this guest on this tour`}>
                            <IconButton style={{ width: "2.5rem", height: "2.5rem" }}>
                                {props.gtuNotificationsPaused === false ?
                                    <PauseAutoCommunications style={{ fontSize: "2.5em" }} />
                                    :
                                    <UnpauseAutoCommunications style={{ fontSize: "2.5em" }} />
                                }
                            </IconButton>
                            <span style={{ fontWeight: 400, fontSize: ".9rem" }}>{(props.gtuNotificationsPaused === false ? 'Pause' : 'Unpause') + (aiAutoRepliesOn() ? " tour notifications & AI auto-replies" : " all tour notifications")}</span>
                        </MenuItem>
                    }
                    <Divider />
                    <MenuItem onClick={props.channelSubjectID ? e => {handleClose(); props.toggleNotesPane(e)} : null}>
                        <IconButton style={{ width: "2.5rem", height: "2.5rem" }}>
                            {props.channelSubjectID ? <NewNote style={{ fontSize: "2.5em" }} /> : <NewNoteDisabled style={{ fontSize: "2.5em" }} />}
                        </IconButton>
                        <span style={{ fontWeight: 400, fontSize: ".9rem" }}>Add internal note</span>
                    </MenuItem>
                    <MenuItem onClick={e => {!forceInternalTagsClick && setForceInternalTagsClick(e.currentTarget);}} onKeyDown={(e) => {e.stopPropagation();}}>
                        <InternalTagsMenu
                            selectedScreen={props.selectedScreen}
                            channelSubjectID={props.channelSubjectID}
                            channelUniqueName={props.channelUniqueName}
                            internalTags={props.internalTags}
                            setInternalTags={props.setInternalTags}
                            style={{ marginLeft: "0" }}
                            forceInternalTagsClick={forceInternalTagsClick}
                            setForceInternalTagsClick={setForceInternalTagsClick}
                        />
                        <span style={{ fontWeight: 400, fontSize: ".9rem" }}>Add internal tags</span>
                    </MenuItem>
                    <MenuItem onClick={props.channelSubjectID ? e => {handleClose(); props.toggleParticipantsList(e); } : null}>
                        {
                            // hide participants list in announcements if guest (so that they can't see other travelers) 
                            (props.channelSubjectID !== "announcements" || props.userIsGuest === false) && (
                                <IconButton style={{ cursor: props.channelSubjectID ? 'pointer' : "default", width: "2.5rem", height: "2.5rem", marginBottom: 1 }}>
                                    {props.channelSubjectID ? <ViewParticipants style={{ fontSize: "2.5em" }} /> : <ViewParticipantsDisabled style={{ fontSize: "2.5em" }} />}
                                </IconButton>
                            )
                        }
                        <span style={{ fontWeight: 400, fontSize: ".9rem" }}>View participants</span>
                    </MenuItem>
                    {canForward &&
                        <MenuItem onClick={() => { handleClose(); setForwardOpen(true); }}>
                            <IconButton style={{ width: "2.5rem", height: "2.5rem" }}>
                                <ForwardToInboxOutlinedIcon style={{ fontSize: "1em", color: cv('accent', "#007AFF") }} />
                            </IconButton>
                            <span style={{ fontWeight: 400, fontSize: ".9rem" }}>Forward by email</span>
                        </MenuItem>
                    }
                    {!isNaN(props.channelSubjectID) &&
                        <span>
                            <MenuItem
                                component="a"
                                href={"/user/" + props.channelSubjectID}
                                target="_blank"
                                rel="noreferrer"
                                onClick={handleClose}
                                style={{ color: "inherit", textDecoration: "none" }}
                            >
                                <IconButton style={{ cursor: props.channelSubjectID ? 'pointer' : "default", width: "2.5rem", height: "2.5rem" }}>
                                    <LinkOutIcon style={{ fontSize: "2.5em", transform: "scale(1.2)" }} />
                                </IconButton>
                                <span style={{ fontWeight: 400, fontSize: ".9rem" }}>View user profile</span>
                            </MenuItem>

                            {props.subjectUserMagicLink &&
                                <MenuItem onClick={() => {
                                    navigator.clipboard.writeText(props.subjectUserMagicLink);
                                    props.enqueueSnackbar("Link copied to clipboard", { variant: 'success' });
                                    handleClose();
                                }}>
                                    <IconButton style={{ cursor: props.channelSubjectID ? 'pointer' : "default", width: "2.5rem", height: "2.5rem" }} >
                                        <LinkIcon style={{ fontSize: "1em", color: cv('accent', "#007AFF") }} />
                                    </IconButton>
                                    <span style={{ fontWeight: 400, fontSize: ".9rem" }}>Copy magic link for app sign-in</span>

                                </MenuItem>
                            }
                            {props.subjectUserReviewLink &&
                                <MenuItem onClick={() => {
                                    navigator.clipboard.writeText(props.subjectUserReviewLink);
                                    props.enqueueSnackbar("Review link copied to clipboard", { variant: 'success' });
                                    handleClose();
                                }}>
                                    <IconButton style={{ cursor: props.channelSubjectID ? 'pointer' : "default", width: "2.5rem", height: "2.5rem" }} >
                                        <ThumbUpOffAltIcon style={{ fontSize: "1em", color: cv('accent', "#007AFF") }} />
                                    </IconButton>
                                    <span style={{ fontWeight: 400, fontSize: ".9rem" }}>Copy review link</span>
                                </MenuItem>
                            }
                            {props.subjectUserPreCheckinLink &&
                                <MenuItem onClick={() => {
                                    navigator.clipboard.writeText(props.subjectUserPreCheckinLink);
                                    props.enqueueSnackbar("Pre-check-in link copied to clipboard", { variant: 'success' });
                                    handleClose();
                                }}>
                                    <IconButton style={{ cursor: props.channelSubjectID ? 'pointer' : "default", width: "2.5rem", height: "2.5rem" }} >
                                        <HowToRegIcon style={{ fontSize: "1em", color: cv('accent', "#007AFF") }} />
                                    </IconButton>
                                    <span style={{ fontWeight: 400, fontSize: ".9rem" }}>Copy pre-check-in link</span>
                                </MenuItem>
                            }
                        </span>
                    }
                    <Divider />
                    <Tooltip title={"Mark the conversation as closed. If new messages appear in the channel, the conversation will automatically reopen."} arrow placement="top">
                    <div style={{ marginLeft: "0.9em", marginTop: "8px" }}>
                        <Checkbox
                            style={{marginBottom:2}}
                            checked={props.issuesState === 0}
                            onChange={props.handleIssuesStateChange}
                        />
                        <span style={{ fontWeight: 400, fontSize: ".9rem"}}>Close conversation&nbsp;</span>
                        <HelpOutlineIcon style={{ cursor: "pointer", float: "right", marginRight: "15px", marginTop: "8px", color: cv('text-secondary', "#5F5F60") }} />
                    </div>
                    </Tooltip>
                </MenuList>

            </StyledMenu>
            {canForward &&
                <ForwardConversationDialog
                    open={forwardOpen}
                    onClose={() => setForwardOpen(false)}
                    conversationId={props.channelUniqueName}
                />
            }
        </span>
    );
}
